import { Field, Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import { Environment } from 'src/Environment';

import { MiniAppFocusWrapper } from '../miniapp-focus-wrapper/miniapp-focus-wrapper';
import { FloatingInput } from '../index';
import MerchantAgreementLink from 'src/containers/onboarding/merchant-agreement-link/merchant-agreement-link';
import {
  isValidEmail,
  isValidEmailTradeRoot,
  validateEmailTradeRoot,
} from 'src/helpers/validation';
import './salesforce-request-otp-form.scss';
import Captcha from '../captcha/captcha';

export default function SalesforceRequestOtpForm({
  onFormLoad,
  requestOtp,
  onOtpSent,
  onReferenceNumberIncorrect,
  onApplicationAlreadySubmitted,
  onTechnicalError,
  automationIdPrefix,
  captcha = false,
  initialEmail = '',
  initialReferenceNumber = '',
}) {
  const [tealiumFormStarted, setTealiumFormStarted] = useState(false);
  const emailInputRef = useRef();
  const REFERENCE_NUMBER_LENGTH = 10;
  const [captchaPassed, setCaptchaPassed] = useState(
    Environment.current?.disabledFeatures?.captcha || !captcha,
  );

  return (
    <div className="salesforce-request-otp-form">
      <Formik
        initialValues={{
          email: initialEmail,
          referenceNumber: initialReferenceNumber,
        }}
        validate={validateForm}
        onSubmit={submitForm}
        validateOnChange={true}
        validateOnBlur={false}
        enableReinitialize={true}
      >
        {({ values, isSubmitting }) => (
          <Form>
            <Field
              name="email"
              type="text"
              placeholder="Email address"
              component={MiniAppFocusWrapper}
              innerComponent={FloatingInput}
              automationid={`${automationIdPrefix}_Field_Email`}
              autoFocus
              innerRef={emailInputRef}
              className="top-field"
            />
            <Field
              name="referenceNumber"
              type="text"
              maxLength={REFERENCE_NUMBER_LENGTH}
              placeholder="Reference number"
              component={MiniAppFocusWrapper}
              innerComponent={FloatingInput}
              automationid={`${automationIdPrefix}_Field_Reference_Number`}
              className="bottom-field"
            />

            {captcha && !Environment.current?.disabledFeatures?.captcha && (
              <div className="captcha has-padding-bottom-4">
                <Captcha onCaptchaPassed={() => setCaptchaPassed(true)} />
              </div>
            )}

            <MerchantAgreementLink className="small-text-agreement-with-link hide-desktop" />

            <div className="has-text-centered continue-button-otp">
              <button
                className="button is-primary is-rounded red-continue-button button-continue"
                type="submit"
                automationid={`${automationIdPrefix}_Button_Continue`}
                disabled={isSubmitting || !isFormValid(values) || !captchaPassed}
              >
                Continue
              </button>
            </div>

            <MerchantAgreementLink className="small-text-agreement-with-link hide-mobile" />
          </Form>
        )}
      </Formik>
    </div>
  );

  async function submitForm(values, helpers) {
    const { setSubmitting, setFieldError } = helpers;
    const { error } = await requestOtp(values.email, values.referenceNumber);

    if (error === 'Record not exist.') {
      setFieldError('referenceNumber', 'Reference number is incorrect');
      onReferenceNumberIncorrect();
      emailInputRef.current.focus();
    } else if (error === 'Application is already finished') {
      setFieldError('referenceNumber', 'Your application has already been submitted.');
      onApplicationAlreadySubmitted();
      emailInputRef.current.focus();
    } else if (error != null) {
      onTechnicalError('unrecognized error received in response to OTP post request');
    } else {
      onOtpSent();
    }
    setSubmitting(false);
  }

  function isFormValid(values) {
    return (
      isValidEmail(values.email) &&
      isValidEmailTradeRoot(values.email) &&
      isValidReferenceNumber(values.referenceNumber) &&
      !(!values.email || !values.referenceNumber)
    );
  }

  function validateForm(values) {
    if (!tealiumFormStarted) {
      onFormLoad();
      setTealiumFormStarted(true);
    }

    const errors = {};

    const emailTradeRootValidationError = validateEmailTradeRoot(values.email);

    if (emailTradeRootValidationError) {
      errors.email = values.email ? emailTradeRootValidationError : null;
    } else {
      const invalidEmail = !isValidEmail(values.email);
      if (invalidEmail) {
        errors.email = values.email ? 'The email address you have entered is invalid' : null;
      }
    }

    const invalidReferenceNumber = !isValidReferenceNumber(values.referenceNumber);

    if (invalidReferenceNumber) {
      errors.referenceNumber = values.referenceNumber
        ? 'The reference number you have entered is invalid'
        : null;
    }

    return errors;
  }

  function isValidReferenceNumber(referenceNumber) {
    return referenceNumber && /^RN[A-Z0-9]{8}$/.test(referenceNumber);
  }
}
